/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, ViewEncapsulation, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterModule } from '@angular/router';
import { PageHeaderComponent, PageStateComponent, ThaiDatePipe } from '@alfresco/aca-shared';
import { TranslatePipe } from '@ngx-translate/core';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ProcessDefinition, TemplateInstance, WorkflowService, WorkflowTemplate } from './workflow.service';
import { flowSteps } from './flow-graph';
import { WorkflowVisibilityDialogComponent } from './workflow-visibility-dialog.component';

/** One management row — a custom template or an engine process-definition. */
interface ManageRow {
  kind: 'template' | 'engine';
  id: string;
  name: string;
  category: string;
  version: number;
  status: 'active' | 'draft' | 'disabled';
  instances: number;
  updatedAt?: string;
  steps: number;
  template?: WorkflowTemplate;
}

/**
 * Workflow Management (WF-09) — the administrator's catalogue.
 *
 * Custom templates come from {@link WorkflowService}'s template store; the
 * engine's own published definitions are listed read-only beside them (they
 * are managed in the repository's workflow console, not here). Editing a
 * template that has running instances is versioned rather than overwritten —
 * {@link WorkflowService.saveTemplate} bumps the version and keeps the old
 * snapshot, so in-flight instances keep the definition they started on.
 */
@Component({
  selector: 'app-workflow-manage',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    MatIconModule,
    MatButtonModule,
    MatDialogModule,
    MatMenuModule,
    MatTooltipModule,
    MatProgressBarModule,
    PageHeaderComponent,
    PageStateComponent,
    ThaiDatePipe,
    TranslatePipe
  ],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './workflow-manage.component.html',
  styles: []
})
export class WorkflowManageComponent implements OnInit {
  /**
   * Whether the catalogue lives inside the settings page — it then drops its
   * own page title, because the settings section already names it.
   */
  @Input() embedded = false;

  private readonly wf = inject(WorkflowService);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);

  readonly loading = signal(true);
  readonly rows = signal<ManageRow[]>([]);
  /** Template flows from the jobs sidecar — every flow, for an administrator. */
  private readonly instances = signal<TemplateInstance[]>([]);
  search = '';
  filterCategory = '';
  filterStatus = '';

  readonly categories = computed(() =>
    [
      ...new Set(
        this.rows()
          .map((r) => r.category)
          .filter(Boolean)
      )
    ].sort()
  );

  readonly kpi = computed(() => {
    const all = this.rows();
    const instances = this.instances();
    return {
      total: all.length,
      active: all.filter((r) => r.status === 'active').length,
      draft: all.filter((r) => r.status === 'draft').length,
      running: instances.filter((i) => i.status === 'running').length,
      error: instances.filter((i) => i.status === 'returned').length
    };
  });

  /** search and the filters are plain ngModel fields — a method, so CD re-evaluates. */
  visible(): ManageRow[] {
    const term = this.search.trim().toLowerCase();
    return this.rows().filter(
      (r) =>
        (!term || r.name.toLowerCase().includes(term)) &&
        (!this.filterCategory || r.category === this.filterCategory) &&
        (!this.filterStatus || r.status === this.filterStatus)
    );
  }

  ngOnInit(): void {
    this.reload();
  }

  reload(): void {
    this.loading.set(true);
    forkJoin({
      // only definitions someone actually deployed here — Alfresco's stock
      // activiti* models are engine internals, not rows an admin maintains
      defs: this.wf.listProcessDefinitions().pipe(catchError(() => of([] as ProcessDefinition[]))),
      templates: this.wf.ensureTemplatesLoaded().pipe(catchError(() => of([] as WorkflowTemplate[]))),
      instances: this.wf.listInstances()
    }).subscribe(({ defs, instances }) => {
      this.instances.set(instances);
      const rows: ManageRow[] = [];

      for (const tpl of this.wf.getTemplates()) {
        rows.push({
          kind: 'template',
          id: tpl.id,
          name: tpl.name,
          category: tpl.category || 'custom',
          version: tpl.version ?? 1,
          // a drawn flow nobody has published, with no steps to fall back on, cannot be started: that is what draft means
          status: tpl.enabled === false ? 'disabled' : WorkflowService.flowState(tpl) === 'draft' && !tpl.steps.length ? 'draft' : 'active',
          instances: instances.filter((i) => i.templateId === tpl.id && i.status === 'running').length,
          updatedAt: tpl.updatedAt,
          steps: tpl.graph ? flowSteps(tpl.graph).length : tpl.steps.length,
          template: tpl
        });
      }

      for (const def of defs) {
        rows.push({
          kind: 'engine',
          id: def.id,
          name: def.name || def.key,
          category: 'system',
          version: Number(String(def.id).split(':')[1]) || 1,
          status: 'active',
          instances: 0,
          steps: 1
        });
      }

      this.rows.set(rows);
      this.loading.set(false);
    });
  }

  /** Edit in the existing designer — versioned automatically on save. */
  edit(row: ManageRow): void {
    void this.router.navigate(['/build-workflow'], { queryParams: { edit: row.id } });
  }

  /** The visibility gate, editable in place — the designer exposes the same controls. */
  visibility(row: ManageRow): void {
    const tpl = row.template;
    if (!tpl) {
      return;
    }
    this.dialog
      .open(WorkflowVisibilityDialogComponent, { data: { template: tpl } })
      .afterClosed()
      .subscribe((saved) => {
        if (saved) {
          this.reload();
        }
      });
  }

  duplicate(row: ManageRow): void {
    const tpl = row.template;
    if (!tpl) {
      return;
    }
    const copy: WorkflowTemplate = {
      ...tpl,
      id: `tpl-${Date.now()}`,
      name: `${tpl.name} (copy)`,
      version: undefined,
      versions: undefined,
      steps: tpl.steps.map((s) => ({ ...s, id: `${s.id}-c${Date.now()}` }))
    };
    this.wf.saveTemplate(copy).subscribe(() => this.reload());
  }

  toggle(row: ManageRow): void {
    const tpl = row.template;
    if (!tpl) {
      return;
    }
    this.wf.setTemplateEnabled(row.id, tpl.enabled === false).subscribe(() => this.reload());
  }

  remove(row: ManageRow): void {
    if (row.kind !== 'template' || row.instances > 0) {
      return;
    }
    this.wf.deleteTemplate(row.id).subscribe(() => this.reload());
  }

  create(): void {
    void this.router.navigate(['/build-workflow']);
  }
}
